import { useState, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { fetchStores } from 'utils/api/storeApi'
import { fetchAggregators } from 'utils/api/aggregatorApi'

import {
  Button,
  Spinner,
  DropdownControl,
  MultiSelectDropdown,
} from 'components/common'
import CsvFileUploader from 'components/fileUploader/fileUploader'
import Modal from 'components/modal'
import { InputOptions, CheckboxOptions } from 'types/input'
import SwitchField from 'components/bundleProduct/switchField'
import {
  cloneInventoryToStoreApi,
  ClonePricePayload,
  clonePricingToStoreApi,
  getUploadUrlToCloneInventoryPricing,
  uploadFileToPresignedUrl,
} from 'utils/api/adminApi'
import toast from 'react-hot-toast'
import { handleError } from 'utils/helpers/handleError'
import {
  CloneModule,
  validCloneFormDataSchema,
  CloneFormData,
} from 'utils/schema/cloneInventoryPriceSchema'

export const Icons = {
  Info: () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  ArrowRight: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
}

/* ---------------- Component ---------------- */

export default function CloneInventoryPrice() {
  const [stores, setStores] = useState<InputOptions[]>([])
  const [aggregators, setAggregators] = useState<InputOptions[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(
    undefined,
  )
  const [resetMultiSelects, setResetMultiSelects] = useState(false)

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<CloneFormData>({
    resolver: yupResolver(validCloneFormDataSchema),
    defaultValues: {
      module: CloneModule.INVENTORY,
      copyAllBarcode: true,
      toStores: [],
      toAggregators: [],
    },
  })

  const formValues = watch()
  const { copyAllBarcode, file, toStores = [], toAggregators = [] } = formValues

  /* ---------------- API Fetching (Memoized) ---------------- */

  const getData = async () => {
    setLoading(true)
    try {
      const [storeRes, aggRes] = await Promise.all([
        fetchStores({
          limit: 500,
          page: 1,
          isActive: true,
          isThirdPartyStore: false,
        }),
        fetchAggregators({ limit: 500, page: 1, isActive: true }),
      ])

      setStores(
        storeRes.data?.docs?.map((s: any) => ({
          label: s.storeName,
          value: s._id,
        })) || [],
      )

      setAggregators(
        aggRes.data?.docs?.map((a: any) => ({
          label: a.aggregatorName,
          value: a._id,
        })) || [],
      )
    } catch (error) {
      console.error('Error loading clone data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getData()
  }, [])

  const memoizedStores = useMemo(() => stores, [stores])
  const memoizedAgg = useMemo(() => aggregators, [aggregators])

  const storeCheckboxOptions: CheckboxOptions[] = useMemo(
    () =>
      stores.map((s) => ({
        label: s.label,
        value: s.value as string,
        disabled: false,
      })),
    [stores],
  )

  const aggregatorCheckboxOptions: CheckboxOptions[] = useMemo(
    () =>
      aggregators.map((a) => ({
        label: a.label,
        value: a.value as string,
        disabled: false,
      })),
    [aggregators],
  )

  const sourceStoreLabel = useMemo(
    () => memoizedStores.find((s) => s.value === formValues.fromStore)?.label,
    [formValues.fromStore, memoizedStores],
  )

  const destStoreLabels = useMemo(
    () =>
      memoizedStores
        .filter((s) => toStores.includes(s.value as string))
        .map((s) => s.label),
    [toStores, memoizedStores],
  )

  const sourceAggLabel = useMemo(
    () => memoizedAgg.find((a) => a.value === formValues.fromAggregator)?.label,
    [formValues.fromAggregator, memoizedAgg],
  )

  const destAggLabels = useMemo(
    () =>
      memoizedAgg
        .filter((a) => toAggregators.includes(a.value as string))
        .map((a) => a.label),
    [toAggregators, memoizedAgg],
  )

  const isPricingModuleSelected = formValues?.module === CloneModule.PRICING

  const destinationPairCount = isPricingModuleSelected
    ? toStores.length * toAggregators.length
    : toStores.length

  /* ---------------- Submit ---------------- */

  const onFinalSubmit = async (data: CloneFormData) => {
    const toastId = toast.loading('Initializing...')
    try {
      let s3Key = null

      if (!data?.copyAllBarcode) {
        toast.loading('Getting upload URL...', { id: toastId })
        const result = await getUploadUrlToCloneInventoryPricing({
          fileName: data.file!.name,
          fileType: data.file!.type,
          module: data.module,
        })

        if (result?.url) {
          const expiresInMin = result.expiresIn
            ? Math.ceil(result.expiresIn / 60)
            : 5

          toast.loading(
            `Uploading file... (link expires in ${expiresInMin} min)`,
            { id: toastId },
          )
          setUploadProgress(0)

          await uploadFileToPresignedUrl({
            file: data.file!,
            uploadUrl: result.url,
            onProgress: (percent) => setUploadProgress(percent),
          })

          setUploadProgress(undefined)
          s3Key = result.key
        }
      }

      toast.loading(
        `${
          isPricingModuleSelected ? 'Cloning pricing' : 'Cloning inventory'
        } data...`,
        { id: toastId },
      )

      if (isPricingModuleSelected) {
        await clonePricingToStoreApi(data as ClonePricePayload, s3Key)
      } else {
        await cloneInventoryToStoreApi(data as any, s3Key)
      }

      toast.success(
        `${
          isPricingModuleSelected ? 'Pricing' : 'Inventory'
        } Data Cloning Initiated`,
        { id: toastId },
      )
      reset()
      setResetMultiSelects((prev) => !prev)
      setIsModalOpen(false)
    } catch (error) {
      setUploadProgress(undefined)
      handleError(error, toastId)
    }
  }

  if (loading) return <Spinner />

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Clone Data</title>
      </Helmet>

      <div className="mx-auto max-w-7xl p-2">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Clone Inventory & Pricing
            </h1>
            <p className="text-sm text-gray-500">
              Sync inventory or pricing from one store/aggregator to multiple
              destination stores and aggregators
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <form
            onSubmit={handleSubmit(() => setIsModalOpen(true))}
            className="space-y-6"
          >
            {/* Module */}
            <div className="grid gap-4 md:grid-cols-3">
              <DropdownControl
                searchable={false}
                label="Module"
                name="module"
                control={control}
                options={[
                  { label: 'Inventory', value: CloneModule.INVENTORY },
                  { label: 'Pricing', value: CloneModule.PRICING },
                ]}
              />
            </div>

            {/* Store + Aggregator Grouped Section */}
            <div className="flex items-start gap-6">
              {/* SOURCE BLOCK */}
              <div className="flex-1 rounded-xl border border-dashed border-gray-300 p-4">
                <div className="space-y-4">
                  <DropdownControl
                    label="Source Store"
                    name="fromStore"
                    control={control}
                    error={errors.fromStore?.message}
                    options={stores}
                  />

                  {isPricingModuleSelected && (
                    <DropdownControl
                      label="Source Aggregator"
                      name="fromAggregator"
                      control={control}
                      options={aggregators}
                      error={errors.fromAggregator?.message}
                    />
                  )}
                </div>
              </div>

              {/* CENTER ARROW */}
              <div className="flex items-center justify-center pt-8 text-gray-400">
                <Icons.ArrowRight />
              </div>

              {/* DESTINATION BLOCK */}
              <div className="flex-1 rounded-xl border border-dashed border-gray-300 p-4">
                <div className="space-y-4">
                  <MultiSelectDropdown
                    label="Destination Stores"
                    name="toStores"
                    setValue={setValue}
                    error={errors.toStores?.message as string}
                    options={storeCheckboxOptions}
                    includeSelectAll={storeCheckboxOptions.length > 0}
                    reset={resetMultiSelects}
                    required
                  />

                  {isPricingModuleSelected && (
                    <MultiSelectDropdown
                      label="Destination Aggregators"
                      name="toAggregators"
                      setValue={setValue}
                      error={errors.toAggregators?.message as string}
                      options={aggregatorCheckboxOptions}
                      includeSelectAll={aggregatorCheckboxOptions.length > 0}
                      reset={resetMultiSelects}
                      required
                    />
                  )}
                </div>
              </div>
            </div>

            {destinationPairCount > 0 && (
              <p className="text-xs text-gray-500">
                This will clone data to <strong>{destinationPairCount}</strong>{' '}
                {isPricingModuleSelected
                  ? 'store + aggregator combination(s)'
                  : 'store(s)'}
                .
              </p>
            )}

            {/* Mode Toggle */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <SwitchField
                control={control}
                name="copyAllBarcode"
                label="Full Clone"
                description="Overwrites all data in destination store"
                defaultValue={true}
              />
            </div>

            {/* File Upload */}
            {!copyAllBarcode && (
              <div className="rounded-xl border border-dashed border-gray-300 p-4">
                <CsvFileUploader
                  label="Upload Barcode File"
                  selectedFileName={file?.name}
                  uploadProgress={uploadProgress}
                  error={errors.file?.message}
                  onFileSelect={(f) => {
                    setValue('file', f as File, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                  onError={(msg) => {
                    if (msg) {
                      setError('file', { type: 'manual', message: msg })
                    } else {
                      clearErrors('file')
                    }
                  }}
                />
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end pt-4">
              <Button buttonText="Clone Data" />
            </div>
          </form>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="Clone Data"
        width={520}
      >
        <div className="space-y-6 py-2">
          {/* Source */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase text-gray-400">
              Source
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-800">
                {sourceStoreLabel}
              </span>
              {isPricingModuleSelected && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                  {sourceAggLabel}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 text-gray-400">
            <div className="h-px flex-1 bg-gray-200" />
            <Icons.ArrowRight />
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          {/* Destinations */}
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase text-gray-400">
              Destinations ({destinationPairCount})
            </span>

            {isPricingModuleSelected ? (
              <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-100">
                {destStoreLabels.map((storeLabel) => (
                  <div
                    key={storeLabel}
                    className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                  >
                    <span className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800">
                      {storeLabel}
                    </span>

                    <div className="flex flex-1 flex-wrap justify-end gap-1.5">
                      {destAggLabels.map((aggLabel) => (
                        <span
                          key={aggLabel}
                          className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600"
                        >
                          {aggLabel}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto rounded-xl border border-gray-100 p-3">
                {destStoreLabels.map((storeLabel) => (
                  <span
                    key={storeLabel}
                    className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-800"
                  >
                    {storeLabel}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            {copyAllBarcode ? (
              <div className="flex gap-3 rounded-xl border border-red-100 bg-red-50/50 p-4">
                <div>
                  <p className="text-sm font-semibold text-red-800">
                    Critical: Full Data Reset
                  </p>
                  <p className="text-xs leading-relaxed text-red-600/80">
                    This action is irreversible. All records in{' '}
                    <strong>each</strong> of the{' '}
                    <strong>{destinationPairCount}</strong> destination{' '}
                    {isPricingModuleSelected
                      ? 'store + aggregator combinations'
                      : 'stores'}{' '}
                    will be deleted first, and then replaced with data from the
                    source. Each destination is processed independently — a
                    failure on one will not affect the others.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                <Icons.Info />
                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    Selective Update
                  </p>
                  <p className="text-xs leading-relaxed text-blue-600/80">
                    Existing records matching the uploaded barcodes in each of
                    the <strong>{destinationPairCount}</strong> destination{' '}
                    {isPricingModuleSelected
                      ? 'store + aggregator combinations'
                      : 'stores'}{' '}
                    will be deleted first, and then updated with the new data.
                    Other records will remain unchanged. Each destination is
                    processed independently — a failure on one will not affect
                    the others.
                  </p>
                  <p className="mt-2 text-xs text-amber-600">
                    The upload URL expires in 5 min — ensure a stable connection
                    before confirming.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-sm text-gray-500"
            >
              Cancel
            </button>
            <Button
              buttonText={isSubmitting ? 'Processing...' : 'Confirm & Clone'}
              onClick={handleSubmit(onFinalSubmit)}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}
